/**
 * Physical-column aliases mapping a risk config's name to the BigQuery column
 * that actually holds its data in `plantations_with_risks`.
 *
 * The "canopy" tree-cover-loss risk (system risk id 25,
 * `risk_tree_cover_loss_canopy_v1`) is computed IN PLACE into the existing
 * `risk_tree_cover_loss_v1_*` column — it has no column of its own. Per SUP-357
 * (AGD-301/302/303), the crd-sync pipeline recomputes that column with the
 * canopy-masked `tree_cover_loss_eudrv2` calc whenever the dataset's Cloud
 * Scheduler sets `apply_canopy_filter: true` (e.g. EHH). So there is no
 * `risk_tree_cover_loss_canopy_v1_*` column in BigQuery, and none is intended.
 *
 * But because system risk id 25 carries the canopy *name*, any query that
 * derived a column from it would ask BigQuery for
 * `risk_tree_cover_loss_canopy_v1_value` (which doesn't exist) and fail with
 * "Invalid query parameters". This map resolves that name to the real
 * `risk_tree_cover_loss_v1` column where the canopy data already lives. The
 * logical risk name/display is left unchanged, so the dashboard still presents
 * it as canopy — with correct canopy numbers.
 */
export const RISK_COLUMN_ALIASES: Readonly<Record<string, string>> = {
  risk_tree_cover_loss_canopy_v1: 'risk_tree_cover_loss_v1'
};

/**
 * Maps a risk config name to the BigQuery column base actually materialized in
 * `plantations_with_risks`.
 *
 * Use ONLY where a risk name is turned into a physical column reference — a
 * SELECT source expression, a WHERE/AND filter key, or an aggregate key. Do NOT
 * use it for result-set keys, column aliases, display labels, or layer
 * identity: those keep the original risk name so the risk is still surfaced as
 * canopy end to end.
 */
export function materializedRiskColumn(riskName: string): string;
export function materializedRiskColumn(
  riskName: string | undefined
): string | undefined;
export function materializedRiskColumn(
  riskName: string | undefined
): string | undefined {
  if (riskName === undefined) {
    return riskName;
  }
  return Object.hasOwn(RISK_COLUMN_ALIASES, riskName)
    ? RISK_COLUMN_ALIASES[riskName]
    : riskName;
}
